import React from "react";
import { useState, useEffect } from "react";
import styled from "styled-components";
import GrayBoxes from "../../assets/gray-boxes.svg";
import { Icon } from "@vds/icons";
import { Input } from "@vds/inputs";
import { Body } from "@vds/typography";
import { FieldArray, useFormikContext, Field, ErrorMessage } from "formik";
import { IconButton, TextField } from "@mui/material";
import { useLocation } from "react-router-dom";
import { handleSiteLevelTagsDelete } from "../Sites/SiteService";
import { useSelector, useDispatch } from "react-redux";
import { showNotificationBanner } from "../../store/actions";
import { validateNameField } from "./sitesUtil";
import styles from "./components/basicForm.module.css";
import { Modal, ModalBody, ModalTitle, ModalFooter } from "@vds/modals";
import { Title } from "@vds/typography";
import { Notification } from "@vds/notifications";
import { ButtonGroup } from "@vds/buttons";
import { NAMING_VALIDATION_ERROR } from "../../global/constants/errorConstants";
import { device } from "../../styles/breakpoints";

const WrapperForTiles = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 11px;
  width: 100%;
  height: auto;
  overflow: visible;
`;

const BaseFrame = styled.div`
  /* Mobile-first: container with vertical line */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  height: auto;
  position: relative;
  overflow: visible;

  &::before {
    content: "";
    position: absolute;
    left: 10%;
    top: 80px;
    bottom: 0;
    width: 1px;
    background-color: #d8dada;

    @media ${device.tablet} {
      left: 25%;
    }
  }
`;

const SavedFloorPlanNameFrame = styled.div`
  /* Mobile-first: full width */
  display: flex;
  width: 100%;
  align-items: center;
  height: auto;

  @media ${device.tablet} {
    max-width: none;
  }
`;

export const FrameForRectangles = styled.div`
  display: flex;
  padding: 4px;
  margin-top: 20px;
  margin-right: 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3px;
  align-self: stretch;
  border-radius: 4px 0px 0px 4px;
  border: 1px solid var(--gray-44, #6f7171);
  background: var(--gray-85, #d8dada) url(${GrayBoxes}) no-repeat center;
  background-size: calc(100% - 8px);
  width: 21px;
  height: 44px;
`;

const MainFrameForAreas = styled.div`
  /* Mobile-first: flexible width with proper spacing */
  display: flex;
  flex-direction: column;
  width: 85%;
  height: auto;
  align-items: flex-start;
  margin-left: 15%;
  gap: 12px;
  overflow: visible;

  @media ${device.tablet} {
    width: auto;
    margin-left: 27%;
  }
`;

const AreaNameFrame = styled.div`
  /* Mobile-first: flexible layout for input + icons */
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding-left: 20px;
  overflow: visible;

  /* Ensure icon button is visible and doesn't shrink */
  & > button {
    flex: 0 0 auto;
    min-width: 48px;
  }

  @media ${device.tablet} {
    padding-left: 0;
    gap: 8px;
  }
`;

const AdditionalAreaFrame = styled.div`
  /* Mobile-first: "Add area" button frame */
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  width: auto;
  height: auto;
  margin-left: 15%;
  margin-top: 10px;
  cursor: pointer;

  @media ${device.tablet} {
    margin-left: 28%;
  }
`;

const AddText = styled.h2`
  color: #000;
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Verizon-NHG-eDS";
  font-size: 16px;
  font-style: normal;
  font-weight: ${(props) => (props.boldText ? 700 : 400)};
  line-height: 20px;
  letter-spacing: 0.5px;
  line-height: 1;
  margin: 0;
  // margin-left: 27%;
  vertical-align: middle;
  cursor: pointer;
`;

const MainFrameForSubAreas = styled.div`
  /* Mobile-first: proper spacing for sub-areas with line */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  width: 100%;
  border-left: 2px solid #d8dada;
  margin-left: 20px;
  margin-top: 15px;
  padding-left: 20px;
  overflow: visible;

  @media ${device.tablet} {
    margin-left: 15px;
    padding-left: 15px;
  }
`;

const SubAreaNameFrame = styled.div`
  /* Mobile-first: flexible layout for sub-area input + icons */
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: auto;
  overflow: visible;

  /* Ensure icon button is visible and doesn't shrink */
  & > button {
    flex: 0 0 auto;
    min-width: 48px;
  }

  @media ${device.tablet} {
    gap: 8px;
  }
`;

const AdditionalSubAreaFrame = styled.div`
  /* Mobile-first: flexible container for "Add sub-area" button */
  display: flex;
  align-items: center;
  flex-wrap: wrap; /* Allow wrapping on very small screens */
  gap: 8px;
  align-self: stretch;
  width: 100%;
  height: auto;
  overflow: visible;

  @media ${device.tablet} {
    gap: 10px;
    flex-wrap: nowrap;
  }
`;

const OptionalLabel = styled.span`
  /* Mobile-first: visible optional label */
  color: #6f7171;
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
  flex-shrink: 0;

  @media ${device.tablet} {
    font-size: 12px;
  }
`;

export const StyledInputDiv = styled.div`
  /* Mobile-first: flexible, leaves room for icon */
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  max-width: calc(100% - 70px); /* Reserve space for icon button (48px) + gap (12px) + buffer (10px) */
  margin-top: -10px;
  height: auto;
  min-height: 64px;
  gap: 40px;

  @media ${device.tablet} {
    max-width: none; /* Restore original behavior for larger screens */
    margin-top: -10px;
  }
`;

const StyledTextField = styled(TextField)`
  /* Mobile-first: auto-height with multiline support */
  width: 100%;
  margin-top: 10px;
  gap: 0px;
  border-radius: 0px 4px 4px 0px;
  border: 0px 4px 4px 0px;
  opacity: 0px;

  & .MuiInputBase-root {
    min-height: 44px;
    height: auto;
    align-items: flex-start;
  }

  & .MuiInputBase-input {
    overflow: visible;
    word-wrap: break-word;
  }

  &:hover fieldset {
    border-color: black;
  }

  &.Mui-focused fieldset {
    border-color: black;
  }
`;

const ToggleButton = styled.button`
  width: 119px;
  background-color: transparent;
  border-color: gray;
  height: 64px;
  background-color: transparent;
  border-color: #d8dada;
  padding: 0px 20px 0px 20px;
  gap: 8px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 20px;
  margin-right: 10px;
  margin-top: 10px;
`;

function SiteBuilderTile({ isEditable, clearFormSiteTagList, reviewPage, onValidationChange }) {
  const { setFieldValue, values, errors } = useFormikContext();
  const location = useLocation();
  const mode = location.state?.mode || "add";
  const { siteTagList } = useSelector((state) => state?.sitesReducer);
  const cvAppRoisWithCVApps = useSelector(
    (s) => s.polygonRoiReducer.roiListWithCvapps
  );
  const [expandedFloors, setExpandedFloors] = useState(
    values.sites?.length ? values.sites.map(() => true) : []
  );
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const dispatch = useDispatch();
  const [validationErrors, setValidationErrors] = useState({});
  useEffect(() => {
    if (values.sites?.length) {
      setExpandedFloors(values.sites.map(() => true));
    }
  }, [values.sites]);

  // Propagate validation state to parent
  useEffect(() => {
    const hasError = Object.values(validationErrors).some(error => !!error);
    onValidationChange?.(hasError);
  }, [validationErrors, onValidationChange]);

  const toggleFloor = (index) => {
    setExpandedFloors((expanded) =>
      expanded.map((exp, i) => (i === index ? !exp : exp))
    );
  };
  const [showBlockedAreaModal, setShowBlockedAreaModal] = useState(false);
  const [blockedSubtitle, setBlockedSubtitle] = useState("");
  const [showConfirmDelAreaModal, setShowConfirmDelAreaModal] = useState(false);
  const [showConfirmDelLastAreaModal, setShowConfirmDelLastAreaModal] = useState(false);
  const [showConfirmDelSubAreaModal, setShowConfirmDelSubAreaModal] =
    useState(false);
  const [confirmDeleteParams, setConfirmDeleteParams] = useState({
    id: null,
    callback: null,
  });
  const [confirmDeleteSubParams, setConfirmDeleteSubParams] = useState({
    id: null,
    callback: null,
  });

  const handleDelete = async (id, cb) => {
    if (mode === "edit") {
      if (!id) {
        cb();
        return;
      }
      const res = await handleSiteLevelTagsDelete(id, activeSession);
      if (res === "" && id) {
        cb();
      }
    } else {
      cb();
    }
  };

  const deleteAreaHandler = async () => {
    const { id, callback } = confirmDeleteParams;
    await handleDelete(id, callback);
    setShowConfirmDelAreaModal(false);
  };

  const deleteSubAreaHandler = async () => {
    const { id, callback } = confirmDeleteSubParams;
    await handleDelete(id, callback);
    setShowConfirmDelSubAreaModal(false);
  };

  const handleNameChange = (fieldPath, rawValue) => {
    setFieldValue(fieldPath, rawValue);

    // Validate format - check raw value to show error for invalid characters
    if (rawValue && !validateNameField(rawValue)) {
      setValidationErrors(prev => ({ ...prev, [fieldPath]: NAMING_VALIDATION_ERROR }));
    } else {
      setValidationErrors(prev => {
        const { [fieldPath]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const floorsWithNoArea = values?.sites
    ?.filter(({ areas }) => !areas?.length)
    .map(({ floorName }) => floorName.indexOf(".") > -1 ? floorName.split(".")[0] : floorName);
  const hasFloorWithNoArea = floorsWithNoArea?.length > 0;

  useEffect(() => {
    if (hasFloorWithNoArea) {
      dispatch(showNotificationBanner({
        type: "Information",
        title: `One area is required per floor: ${floorsWithNoArea.join(", ")}`,
        priority: "critical",
        showNotification: true,
        isDismissible: false,
        hideCloseButton: true,
      }));
    } else {
      dispatch(showNotificationBanner({ showNotification: false }));
    }
    return () => {
      dispatch(showNotificationBanner({ showNotification: false }));
    };
  }, [hasFloorWithNoArea, floorsWithNoArea?.join(",")]);

  return (
    <WrapperForTiles>
      <FieldArray name="sites">
        {() =>
          values?.sites?.length
            ? values.sites.map(({ floorName, areas }, index) => (
                <BaseFrame key={index}>
                  <SavedFloorPlanNameFrame>
                    <ToggleButton
                      data-testid="siteBuilderTile_toggleCaretButton"
                      onClick={() => toggleFloor(index)}
                    >
                      <Body size="large" bold>
                        Floor
                      </Body>
                      {expandedFloors[index] ? (
                        <Icon name="up-caret" />
                      ) : (
                        <Icon name="down-caret" />
                      )}
                    </ToggleButton>
                    {mode === "add" || (!reviewPage && <FrameForRectangles />)}
                    <StyledInputDiv>
                      <Input
                        type="text"
                        label="Floor Name"
                        value={
                          floorName.indexOf(".") > -1
                            ? floorName.split(".")[0]
                            : floorName
                        }
                        className={`${styles.formInput} ${
                          mode === "edit" && !isEditable
                            ? styles.disabledField
                            : null
                        }`}
                        disabled={mode === "edit" && !isEditable}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          setFieldValue(`sites.${index}.floorName`, rawValue);
                        }}
                        readOnly={true}
                      />
                    </StyledInputDiv>
                  </SavedFloorPlanNameFrame>
                  {expandedFloors[index] && (
                    <FieldArray name={`sites.${index}.areas`}>
                      {({ push: pushArea, remove: removeArea }) => (
                        <>
                          {areas?.map(({ areaName, id, subAreas }, idx) => (
                            <MainFrameForAreas key={idx}>
                              <AreaNameFrame>
                                <StyledInputDiv>
                                  <Field
                                    name={`sites.${index}.areas.${idx}.areaName`}
                                  >
                                    {({ field }) => (
                                      <Input
                                        {...field}
                                        type="text"
                                        label="Area name"
                                        value={areaName}
                                        className={`${styles.formInput} ${
                                          mode === "edit" && !isEditable
                                            ? styles.disabledField
                                            : null
                                        }`}
                                        disabled={
                                          mode === "edit" && !isEditable
                                        }
                                        onChange={(e) =>
                                          handleNameChange(
                                            `sites.${index}.areas.${idx}.areaName`,
                                            e.target.value
                                          )
                                        }
                                        error={!!validationErrors[`sites.${index}.areas.${idx}.areaName`]}
                                        errorText={validationErrors[`sites.${index}.areas.${idx}.areaName`] || " "}
                                        readOnly={reviewPage}
                                      />
                                    )}
                                  </Field>
                                </StyledInputDiv>
                                {!(mode === "edit" && !isEditable) &&
                                !reviewPage ? (
                                  <IconButton
                                    data-testid="siteBuilderTile_trashIconForAreaDeletion"
                                    onClick={() => {
                                        const areaObj =
                                          siteTagList?.areas?.find(
                                            (a) => a?.id === id
                                          );

                                        const subAreaObjs =
                                          siteTagList?.subareas?.filter(
                                            (subArea) =>
                                              Array.isArray(subArea?.tagids) &&
                                              subArea?.tagids?.includes(
                                                areaObj?.id
                                              )
                                          );

                                        const allBlockedIds = [
                                          areaObj?.id,
                                          ...subAreaObjs.map((s) => s?.id),
                                        ];

                                        const blockedROI =
                                          cvAppRoisWithCVApps?.find(
                                            (Roi) =>
                                              Array.isArray(Roi?.tagids) &&
                                              Roi?.tagids?.some((tag) =>
                                                allBlockedIds?.includes(tag)
                                              )
                                          );

                                        if (blockedROI) {
                                          if (
                                            blockedROI?.tagids?.includes(
                                              areaObj?.id
                                            ) &&
                                            !subAreaObjs.some((sa) =>
                                              blockedROI.tagids.includes(sa.id)
                                            )
                                          ) {
                                            setBlockedSubtitle(
                                              "There are active subscriptions or ROIs tied to this area. Remove them before deleting."
                                            );
                                          } else {
                                            setBlockedSubtitle(
                                              "There are active subscriptions or ROIs tied to a sub-area under this area. Remove them before deleting."
                                            );
                                          }
                                          setShowBlockedAreaModal(true);
                                          return;
                                        }

                                        setConfirmDeleteParams({
                                          id,
                                          callback: () => {
                                            values.sites[index].areas[
                                              idx
                                            ].subAreas.forEach(
                                              (s) =>
                                                s.id &&
                                                clearFormSiteTagList(
                                                  s.id,
                                                  "subareas"
                                                )
                                            );
                                            removeArea(idx);
                                            id &&
                                              clearFormSiteTagList(id, "areas");
                                          },
                                        });
                                        if (values.sites?.[index].areas?.length === 1) {
                                          setShowConfirmDelLastAreaModal(true);
                                        } else {
                                          setShowConfirmDelAreaModal(true);
                                        }
                                    }}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <Icon
                                      name="trash"
                                      color="#000000"
                                      size="large"
                                    />
                                  </IconButton>
                                ) : null}
                              </AreaNameFrame>
                              <ErrorMessage
                                name={`sites.${index}.areas.${idx}.areaName`}
                                component={"div"}
                                className={styles.errorLabel}
                                style={{ marginLeft: "10px" }}
                              />
                              <FieldArray
                                name={`sites.${index}.areas.${idx}.subAreas`}
                              >
                                {({
                                  push: pushSubArea,
                                  remove: removeSubArea,
                                }) => (
                                  <MainFrameForSubAreas>
                                    {subAreas?.map(
                                      ({ subAreaName, id }, subIdx) => (
                                        <SubAreaNameFrame
                                          key={subIdx}
                                          style={{
                                            width:
                                              mode === "edit" && !isEditable
                                                ? "89%"
                                                : "101%",
                                          }}
                                        >
                                          <StyledInputDiv>
                                            <Input
                                              type="text"
                                              required={true}
                                              error={!!validationErrors[`sites.${index}.areas.${idx}.subAreas.${subIdx}.subAreaName`]}
                                              errorText={validationErrors[`sites.${index}.areas.${idx}.subAreas.${subIdx}.subAreaName`] || " "}
                                              label="Sub Area name"
                                              value={subAreaName}
                                              className={`${styles.formInput} ${
                                                mode === "edit" && !isEditable
                                                  ? styles.disabledField
                                                  : null
                                              }`}
                                              disabled={
                                                mode === "edit" && !isEditable
                                              }
                                              onChange={(e) =>
                                                handleNameChange(
                                                  `sites.${index}.areas.${idx}.subAreas.${subIdx}.subAreaName`,
                                                  e.target.value
                                                )
                                              }
                                              readOnly={reviewPage}
                                            />
                                          </StyledInputDiv>
                                          {!(mode === "edit" && !isEditable) &&
                                          !reviewPage ? (
                                            <IconButton
                                              data-testid="siteBuilderTile_trashIconForSubareaDeletion"
                                              onClick={() => {
                                                {
                                                  const subObj =
                                                    siteTagList.subareas.find(
                                                      (sa) => sa?.id === id
                                                    );
                                                  const subAreaID = subObj?.id;
                                                  if (
                                                    subAreaID &&
                                                    cvAppRoisWithCVApps.some(
                                                      (d) =>
                                                        Array.isArray(
                                                          d.tagids
                                                        ) &&
                                                        d?.tagids?.includes(
                                                          subAreaID
                                                        )
                                                    )
                                                  ) {
                                                    setShowBlockedAreaModal(
                                                      true
                                                    );
                                                    return;
                                                  }
                                                  setConfirmDeleteSubParams({
                                                    id,
                                                    callback: () => {
                                                      removeSubArea(subIdx);
                                                      clearFormSiteTagList(
                                                        id,
                                                        "subareas"
                                                      );
                                                    },
                                                  });
                                                  setShowConfirmDelSubAreaModal(
                                                    true
                                                  );
                                                }
                                              }}
                                              style={{ cursor: "pointer" }}
                                            >
                                              <Icon
                                                name="trash"
                                                color="#000000"
                                                size="large"
                                              />
                                            </IconButton>
                                          ) : null}
                                        </SubAreaNameFrame>
                                      )
                                    )}
                                    {(mode === "add" ||
                                      (mode === "edit" && isEditable)) &&
                                      !reviewPage && (
                                        <AdditionalSubAreaFrame
                                          data-testid="siteBuilderTile_plusIconForAddingSubarea"
                                          onClick={() =>
                                            pushSubArea({
                                              id: null,
                                              subAreaName: "",
                                            })
                                          }
                                        >
                                          <Icon
                                            name="plus"
                                            color="#000000"
                                            size="large"
                                            style={{ cursor: "pointer" }}
                                          />
                                          <AddText>Add sub-area</AddText>
                                          <OptionalLabel>(Optional)</OptionalLabel>
                                        </AdditionalSubAreaFrame>
                                      )}
                                  </MainFrameForSubAreas>
                                )}
                              </FieldArray>
                            </MainFrameForAreas>
                          ))}
                          {(mode === "add" ||
                            (mode === "edit" && isEditable)) &&
                            !reviewPage && (
                              <AdditionalAreaFrame
                                data-testid="siteBuilderTile_plusIconForAddingArea"
                                onClick={() =>
                                  pushArea({
                                    areaName: "",
                                    id: null,
                                    subAreas: [],
                                  })
                                }
                              >
                                <Icon
                                  name="plus"
                                  color="#000000"
                                  size="large"
                                />
                                <AddText>Add area</AddText>
                              </AdditionalAreaFrame>
                            )}
                        </>
                      )}
                    </FieldArray>
                  )}
                </BaseFrame>
              ))
            : null
        }
      </FieldArray>
      <Modal
        opened={showBlockedAreaModal}
        onOpenedChange={(open) => setShowBlockedAreaModal(open)}
      >
        <ModalTitle>Cannot delete area</ModalTitle>
        <ModalBody>
          <Notification
            type="error"
            hideCloseButton={true}
            title="This area cannot be deleted"
            subtitle={blockedSubtitle && blockedSubtitle}
          />
        </ModalBody>
      </Modal>

      <Modal
        opened={showConfirmDelAreaModal}
        onOpenedChange={(open) => setShowConfirmDelAreaModal(open)}
      >
        <ModalTitle>
          <Title size="large" bold>
            Delete area
          </Title>
        </ModalTitle>
        <ModalBody>
          <Notification
            type="warning"
            hideCloseButton={true}
            title="Deleting a area"
            subtitle="Deleting a area will sub-areas that it contains, from the portal"
          />
          <br />
          Are you sure you want to delete this area?
        </ModalBody>
        <ModalFooter>
          <ButtonGroup
            childWidth={"100%"}
            viewport={"desktop"}
            rowQuantity={{ desktop: 2 }}
            data={[
              {
                children: "Cancel",
                size: "medium",
                use: "secondary",
                "data-testid": "siteBuilderTile_cancelButtonDeleteArea",
                onClick: () => setShowConfirmDelAreaModal(false),
              },
              {
                children: "Delete",
                size: "medium",
                use: "primary",
                "data-testid": "siteBuilderTile_deleteButtonDeleteArea",
                onClick: deleteAreaHandler,
              },
            ]}
            alignment={"left"}
          />
        </ModalFooter>
      </Modal>
      {showConfirmDelLastAreaModal && <Modal
        opened={showConfirmDelLastAreaModal}
        onOpenedChange={(open) => setShowConfirmDelLastAreaModal(open)}
      >
        <ModalTitle>
          <Title size="large" bold>
            Delete area
          </Title>
        </ModalTitle>
        <br />
        <br />
        <Notification
          type="error"
          hideCloseButton={true}
          title="Must have at least one area"
          subtitle="A floor must have at least one area at all times"
        />
      </Modal>}
      <Modal
        opened={showConfirmDelSubAreaModal}
        onOpenedChange={(open) => setShowConfirmDelSubAreaModal(open)}
      >
        <ModalTitle>Delete sub-area</ModalTitle>
        <ModalBody>
          <Notification
            type="warning"
            hideCloseButton
            title="Deleting a sub-area"
            subtitle="This will remove the sub-area from the portal."
          />
          <br />
          Are you sure you want to delete this sub-area?
        </ModalBody>
        <ModalFooter>
          <ButtonGroup
            childWidth="100%"
            viewport="desktop"
            rowQuantity={{ desktop: 2 }}
            data={[
              {
                children: "Cancel",
                size: "medium",
                use: "secondary",
                "data-testid": "siteBuilderTile_cancelButtonDeleteSubArea",
                onClick: () => setShowConfirmDelSubAreaModal(false),
              },
              {
                children: "Delete",
                size: "medium",
                use: "primary",
                "data-testid": "siteBuilderTile_deleteButtonDeleteSubArea",
                onClick: deleteSubAreaHandler,
              },
            ]}
            alignment="left"
          />
        </ModalFooter>
      </Modal>
    </WrapperForTiles>
  );
}

export default SiteBuilderTile;
